import type { HTMLAttributes, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tabla del sistema de diseño.
 *
 * En la referencia visual las tablas viven SIEMPRE dentro de una tarjeta: el título y la
 * acción van en el `CardHeader`, la cabecera de columnas es gris tenue en mayúsculas y las
 * filas se separan con hairlines (`border-line`), con un `hover` muy suave.
 *
 * Se compone con las piezas de abajo (no recibe una matriz de datos) para que cada pantalla
 * decida sus celdas: badges de estado, botones de acción, cifras con `tabular`…
 *
 * Para que el scroll horizontal no rompa el radio de la tarjeta, envuélvela con
 * `CardTableArea` (ya existe en `Card.tsx`) o usa el contenedor de `Table`.
 */

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return <table className={cn('w-full border-collapse text-left', className)} {...props} />;
}

export function TableHead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('bg-surface-muted/60', className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={className} {...props} />;
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      className={cn('border-b border-line transition-colors last:border-b-0', className)}
      {...props}
    />
  );
}

export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Alinea a la derecha (acciones, importes). */
  align?: 'left' | 'right';
}

export function TableHeaderCell({
  className,
  align = 'left',
  ...props
}: TableHeaderCellProps) {
  return (
    <th
      scope="col"
      className={cn(
        'px-6 py-3 text-micro font-semibold tracking-wide text-ink-faint uppercase whitespace-nowrap',
        align === 'right' && 'text-right',
        className,
      )}
      {...props}
    />
  );
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: 'left' | 'right';
}

export function TableCell({ className, align = 'left', ...props }: TableCellProps) {
  return (
    <td
      className={cn(
        'px-6 py-3.5 align-middle text-body text-ink-soft',
        align === 'right' && 'text-right',
        className,
      )}
      {...props}
    />
  );
}

/** Celda de acciones (botones a la derecha, sin que se partan en dos líneas). */
export function TableActionsCell({ className, ...props }: TableCellProps) {
  return (
    <TableCell
      align="right"
      className={cn('whitespace-nowrap', className)}
      {...props}
    />
  );
}
